import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Stack, InputAdornment } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TextField } from "@mui/material";
import { FaFileExcel } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa";
import { FaRegCopy } from "react-icons/fa"; // outlined copy icon
import SearchIcon from "@mui/icons-material/Search"; // or any other icon
import { debounce } from "lodash";
import { useCallback } from "react";
import CustomFooter from "./CustomeFooter";
import { useSelector } from "react-redux";

const TableCompo = React.forwardRef(
  (
    {
      columns = [],
      data = [],
      title = "",
      loading = false,
      page = 0, // controlled from parent
      limit = 5, // controlled from parent
      totalCount = 0, // total items from backend
      onTableChange = () => { }, // callback to parent
      rowsPerPageOptions = [5, 10, 25, 50],
      pdfColumnWidths = {}, // ✅ New prop
      extraButtons = null,
      toolbarExtra = null, // optional actions rendered beside the search bar
      stickyColumnFields = [], // fields to keep sticky on horizontal scroll
      getRowHeight,
      multiline = false,
      estimatedRowHeight,
      searchDebounceMs = 2000,
      hideExportButtons = false,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [visibleColumns, setVisibleColumns] = useState(
      columns.filter((col) => !col.hide).map((col) => col.field)
    );

    const package_name = useSelector((state) => state?.appConfig?.configData.package_name || "");

    useEffect(() => {
      const incomingVisibleFields = columns
        .filter((col) => !col.hide)
        .map((col) => col.field);

      setVisibleColumns((prev) => {
        const merged = Array.from(
          new Set([...prev, ...incomingVisibleFields])
        ).filter((field) =>
          columns.some((col) => col.field === field && !col.hide)
        );
        return merged;
      });
    }, [columns]);


    const handleToggleColumn = (field) => {
      setVisibleColumns((prev) =>
        prev.includes(field)
          ? prev.filter((f) => f !== field)
          : [...prev, field]
      );
    };

    const stickyEnabled = Array.isArray(stickyColumnFields) && stickyColumnFields.length > 0;

    const filteredColumns = columns
      .filter((col) => visibleColumns.includes(col.field))
      .map((col) => ({
        ...col,
        ...(col.width != null
      ? { width: col.width, flex: undefined }
      : { flex: col.flex || 1 }),
        minWidth: col.minWidth || 150,
        sortable: col.sortable !== false,
        renderCell: col.render
          ? (params) => col.render(params.value, params.row)
          : undefined,
        headerClassName: stickyColumnFields.includes(col.field)
          ? "custom-header sticky-header"
          : "custom-header",
        cellClassName: [
          "custom-cell",
          stickyColumnFields.includes(col.field) ? "sticky-cell" : "",
          col.alignTop ? "custom-cell-top" : "",
        ]
          .filter(Boolean)
          .join(" "),
      }));

    // ✅ Excel Download
    const handleDownloadExcel = () => {
      try {
        const exportableColumns = columns.filter((col) => !col.hideInExcel);
        const exportData = data.map((row) => {
          const formattedRow = {};
          exportableColumns.forEach((col) => {
            formattedRow[col.headerName || col.field] = row[col.field];
          });
          return formattedRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, `${title || "DataGrid"}_Export.xlsx`);

        toast.success(t("pages.excelDownloadSuccess"));
        // toast.warn("🔓 Please click 'Enable Editing' in Excel to modify the file.");
      } catch (error) {
        console.error("Excel download failed:", error);
        toast.error(t("pages.excelDownloadFailed"));
      }
    };

    // ✅ PDF Download with column width support
    const handleDownloadPDF = () => {
      try {
        const doc = new jsPDF("l", "mm", "a4");

        const exportableColumns = columns.filter((col) => !col.hideInExcel);
        const tableColumnHeaders = exportableColumns.map(
          (col) => col.headerName || col.field
        );
        const tableRows = data.map((row) =>
          exportableColumns.map((col) => row[col.field] ?? "")
        );

        const columnStyles = {};
        exportableColumns.forEach((col, index) => {
          const width = pdfColumnWidths[col.field];
          if (width) {
            columnStyles[index] = { cellWidth: width };
          }
        });

        autoTable(doc, {
          head: [tableColumnHeaders],
          body: tableRows,
          startY: 20,
          margin: { left: 10, right: 10 },
          styles: {
            fontSize: 9,
            cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
            overflow: "linebreak",
          },
          headStyles: {
            fillColor: [63, 81, 181],
            textColor: "#ffffff",
            fontSize: 10,
            halign: "center",
          },
          columnStyles,
          didDrawPage: (data) => {
            doc.setFontSize(14);
            doc.text(title || "DataGrid Export", data.settings.margin.left, 10);
          },
        });

        doc.save(`${title || "DataGrid"}_Export.pdf`);
        toast.success(t("pages.pdfDownloadSuccess"));
      } catch (error) {
        console.error("PDF download failed:", error);
        toast.error(t("pages.pdfDownloadFailed"));
      }
    };
    // download csv
    const handleDownloadCSV = () => {
      try {
        const exportableColumns = columns.filter((col) => !col.hideInExcel);
        const headers = exportableColumns.map(
          (col) => `"${col.headerName || col.field}"`
        );
        const rows = data.map((row) =>
          exportableColumns.map((col) => `"${row[col.field] ?? ""}"`)
        );
        const csvContent = [headers, ...rows]
          .map((r) => r.join(","))
          .join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        saveAs(blob, `${title || "DataGrid"}_Export.csv`);

        toast.success(t("pages.csvDownloadSuccess"));
      } catch (error) {
        console.error("CSV download failed:", error);
        toast.error(t("pages.csvDownloadFailed"));
      }
    };

    // ✅ Copy to Clipboard
    const handleCopyToClipboard = () => {
      const exportableColumns = columns.filter((col) => !col.hideInExcel);
      const headers = exportableColumns
        .map((col) => col.headerName || col.field)
        .join("\t"); // tab-separated

      const rows = data.map((row) =>
        exportableColumns.map((col) => row[col.field] ?? "").join("\t")
      );

      const clipboardText = [headers, ...rows].join("\n");

      navigator.clipboard
        .writeText(clipboardText)
        .then(() => {
          toast.success(t("pages.copySuccess"));
        })
        .catch((err) => {
          toast.error(t("pages.copyFailed"));
          console.error("Failed to copy: ", err);
        });
    };

    const handleSearch = useCallback(
      debounce((value) => {
        onTableChange("search", { searchText: value });
      }, searchDebounceMs),
      [onTableChange, searchDebounceMs]
    );

    useEffect(() => {
      return () => {
        handleSearch.cancel?.();
      };
    }, [handleSearch]);


    return (
      <Box sx={{ mt: 2, width: "100%" }}>
        {/* Enhanced Search Bar */}
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            mb: 3,
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              backgroundColor: "#fff",
              padding: "8px 16px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e8e8e8",
            }}
          >
            <SearchIcon sx={{ color: "#7A3FFF", fontSize: "20px" }} />
            <TextField
              size="small"
              placeholder={t("pages.searchPlaceholder")}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#333",
                  padding: "4px 0",
                  minWidth: "250px",
                  "&::placeholder": {
                    color: "#999",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
          {toolbarExtra}
        </Box>

        <Box
          sx={{
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            transition: "box-shadow 0.3s ease",

            "&:hover": {
              boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            },

            "& .custom-header": {
              backgroundColor: "#7A3FFF !important",
              color: "#fff !important",
              fontWeight: 600,
              fontSize: "14px",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              padding: "16px 20px",
              borderRight: "1px solid rgba(255, 255, 255, 0.2)",
              borderBottom: "2px solid #6a2fef",
              "&:last-of-type": {
                borderRight: "none",
              },
            },

            "& .custom-cell": {
              position: "relative",
              display: "flex",
              alignItems: "center",
              fontSize: "14px",
              fontWeight: 400,
              backgroundColor: "#fff",
              borderRight: "1px solid #e0e0e0",
              borderBottom: "1px solid #e0e0e0",
              padding: "12px 20px",
              color: "#333",
              transition: "background-color 0.2s ease",
            },

            "& .custom-cell-top": {
              alignItems: "flex-start",
              paddingTop: "10px",
              paddingBottom: "10px",
            },

            ...(multiline
              ? {
                  "& .MuiDataGrid-row": {
                    maxHeight: "none !important",
                  },
                  "& .MuiDataGrid-cell": {
                    overflow: "visible !important",
                    alignItems: "flex-start",
                    py: 1,
                    maxHeight: "none !important",
                  },
                  "& .MuiDataGrid-cellContent": {
                    overflow: "visible !important",
                    whiteSpace: "normal !important",
                    textOverflow: "clip !important",
                    lineHeight: "normal",
                    width: "100%",
                  },
                  "& .custom-cell-top": {
                    display: "block !important",
                    alignItems: "flex-start",
                    overflow: "visible !important",
                    height: "auto !important",
                    maxHeight: "none !important",
                  },
                }
              : {}),

            "& .MuiDataGrid-row": {
              transition: "background-color 0.2s ease, transform 0.1s ease",
              "&:hover": {
                backgroundColor: "#f8f7ff !important",
                transform: "translateY(-1px)",
                boxShadow: "0 2px 8px rgba(122, 63, 255, 0.1)",
              },
              "&:nth-of-type(even)": {
                backgroundColor: "#fafafa",
                "&:hover": {
                  backgroundColor: "#f8f7ff !important",
                },
              },
            },

            "& .MuiDataGrid-row .MuiDataGrid-cell:last-child": {
              borderRight: "none",
            },

            "& .MuiDataGrid-columnHeaders": {
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#7A3FFF",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              borderBottom: "2px solid #6a2fef",
            },
            "& .sticky-header": {
              position: "sticky",
              left: 0,
              zIndex: 12,
              backgroundColor: "#7A3FFF !important",
              boxShadow: "2px 0 0 rgba(255, 255, 255, 0.2)",
            },
            "& .sticky-cell": {
              position: "sticky",
              left: 0,
              zIndex: 5,
              backgroundColor: "#fff",
              boxShadow: "2px 0 0 #e0e0e0",
            },
            // More reliable sticky targeting for DataGrid
            ...(stickyEnabled
              ? stickyColumnFields.reduce((acc, field) => {
                  acc[
                    `& .MuiDataGrid-columnHeader[data-field="${field}"]`
                  ] = {
                    position: "sticky",
                    left: 0,
                    zIndex: 12,
                    backgroundColor: "#7A3FFF !important",
                    boxShadow: "2px 0 0 rgba(255, 255, 255, 0.2)",
                  };
                  acc[`& .MuiDataGrid-cell[data-field="${field}"]`] = {
                    position: "sticky",
                    left: 0,
                    zIndex: 6,
                    backgroundColor: "#fff",
                    boxShadow: "2px 0 0 #e0e0e0",
                  };
                  return acc;
                }, {})
              : {}),

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "14px",
            },

            "& .MuiDataGrid-columnHeader": {
              borderRight: "1px solid rgba(255, 255, 255, 0.2)",
              "&:last-of-type": {
                borderRight: "none",
              },
            },

            "& .MuiDataGrid-virtualScroller": {
              maxHeight: multiline ? "none" : "500px",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#7A3FFF",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#6a2fef",
                },
              },
            },

            "& .MuiDataGrid-footerContainer": {
              borderTop: "2px solid #f0f0f0",
              backgroundColor: "#fafafa",
              padding: "12px 20px",
              borderBottomLeftRadius: "16px",
              borderBottomRightRadius: "16px",
            },

            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #e0e0e0",
              borderBottom: "1px solid #e0e0e0",
              outline: "none !important",
            },
            
            "& .MuiDataGrid-cell:last-of-type": {
              borderRight: "none",
            },

            "& .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },

            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={filteredColumns}
            rowCount={totalCount}
            disableVirtualization={stickyEnabled || multiline}
            paginationMode="server"
            paginationModel={{ page, pageSize: limit }}
            onPaginationModelChange={({ page, pageSize }) => {
              if (pageSize !== limit) {
                onTableChange("changeRowsPerPage", {
                  page: 0,
                  rowsPerPage: pageSize,
                });
              } else {
                onTableChange("changePage", {
                  page,
                  rowsPerPage: pageSize,
                });
              }
            }}
            pageSizeOptions={rowsPerPageOptions}
            loading={loading}
            getRowId={(row) => row._id || row.id}
            getRowHeight={multiline ? () => "auto" : getRowHeight}
            getEstimatedRowHeight={
              multiline
                ? () =>
                    typeof estimatedRowHeight === "number"
                      ? estimatedRowHeight
                      : 80
                : undefined
            }
            disableSelectionOnClick
            checkboxSelection={false}
            slots={{
              footer: () => (
                <CustomFooter
                  extraButtons={
                    hideExportButtons && !extraButtons ? null : (
                    <Box sx={{ mb: 1, px: 2 }}>
                      {
                        extraButtons ? (
                          <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                              "& .action-btn": {
                                borderRadius: "6px",
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#333",
                                textTransform: "none",
                                border: "1px solid #d0d0d0",
                                backgroundColor: "#fafafa",
                                transition: "background 0.2s ease, border 0.2s ease",
                              },
                              "& .action-btn:hover": {
                                backgroundColor: "#f0f0f0",
                                border: "1px solid #b5b5b5",
                              },
                            }}
                          >
                            {extraButtons && React.isValidElement(extraButtons) ? extraButtons : null}
                          </Stack>
                        )
                          : (
                          <Stack
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            mt={2}
                            spacing={1.5}
                            sx={{
                              flexWrap: "wrap",
                              gap: 1.5,
                            }}
                          >
                            <Button
                              variant="contained"
                              startIcon={<FaFileExcel />}
                              onClick={handleDownloadCSV}
                              sx={{
                                background: "linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "13px",
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "8px 20px",
                                boxShadow: "0 2px 8px rgba(46, 204, 113, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #229954 0%, #27ae60 100%)",
                                  boxShadow: "0 4px 12px rgba(46, 204, 113, 0.4)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              {t("pages.csv")}
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<FaFileExcel />}
                              onClick={handleDownloadExcel}
                              sx={{
                                background: "linear-gradient(135deg, #2980b9 0%, #3498db 100%)",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "13px",
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "8px 20px",
                                boxShadow: "0 2px 8px rgba(52, 152, 219, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #2471a3 0%, #2980b9 100%)",
                                  boxShadow: "0 4px 12px rgba(52, 152, 219, 0.4)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              {t("pages.excel")}
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<FaFilePdf />}
                              onClick={handleDownloadPDF}
                              sx={{
                                background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "13px",
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "8px 20px",
                                boxShadow: "0 2px 8px rgba(231, 76, 60, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #c0392b 0%, #a93226 100%)",
                                  boxShadow: "0 4px 12px rgba(231, 76, 60, 0.4)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              {t("pages.pdf")}
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<FaRegCopy />}
                              onClick={handleCopyToClipboard}
                              sx={{
                                background: "linear-gradient(135deg, #7A3FFF 0%, #9E3ED7 100%)",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "13px",
                                textTransform: "none",
                                borderRadius: "8px",
                                padding: "8px 20px",
                                boxShadow: "0 2px 8px rgba(122, 63, 255, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #6a2fef 0%, #8b2dd4 100%)",
                                  boxShadow: "0 4px 12px rgba(122, 63, 255, 0.4)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                            >
                              {t("pages.copy")}
                            </Button>
                          </Stack>
                        )
                      }

                    </Box>
                    )
                  }
                />
              ),
            }}
          />
        </Box>
      </Box>
    );
  }
);

export default TableCompo;
